import { motion } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { useLang, scrollToId, waLink, EASE } from "@/lib/site";
import { copy } from "@/lib/content";
import { Portrait } from "./Portrait";
import { DailyQuote } from "./DailyQuote";

const fade = (delay) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, delay, ease: EASE } });

export const Hero = () => {
  const { lang } = useLang();
  const t = copy[lang];
  return (
    <section id="beranda" data-testid="hero-section" className="relative overflow-hidden pt-32 pb-24 lg:pt-40 lg:pb-32">
      <div className="hero-aura pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p {...fade(0.1)} className="font-mono text-xs uppercase tracking-[0.28em] text-amber-300 flex items-center gap-3">
            <span className="h-px w-10 bg-amber-300/60" />{t.heroEyebrow}
          </motion.p>
          <h1 data-testid="hero-title" className="font-display mt-7 text-[3.1rem] leading-[0.98] sm:text-7xl lg:text-[5.6rem] xl:text-[6.4rem] text-slate-50 tracking-[-0.01em]">
            {t.heroLines.map((line, i) => (
              <span key={lang + i} className="block overflow-hidden pb-[0.1em]">
                <motion.span className={`block ${i === 2 ? "italic text-gold" : ""}`} initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p {...fade(0.75)} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300">{t.heroSub}</motion.p>
          <motion.div {...fade(0.9)} className="mt-10 flex flex-col sm:flex-row gap-4">
            <button data-testid="hero-explore-ebooks-button" onClick={() => scrollToId("ebook")} className="btn-gold group inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 font-semibold">
              {t.ctaPrimary}
              <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <a data-testid="hero-whatsapp-button" href={waLink(t.waHello)} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 font-medium text-slate-100">
              <MessageCircle size={18} className="text-amber-300" />{t.ctaSecondary}
            </a>
          </motion.div>
          <DailyQuote />
        </div>
        <div className="lg:col-span-5 px-6 sm:px-0">
          <Portrait badge={t.heroBadge} badgeSub={t.heroBadgeSub} />
        </div>
      </div>
      <motion.div {...fade(1.3)} className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
        {t.scroll}<span className="scroll-line h-10 w-px bg-amber-200/40" />
      </motion.div>
    </section>
  );
};
